import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { TUTORIALS, TOUR_ROLES } from "@/lib/tutorials";
import { ZOOD_MASCOT } from "@/lib/brand";
import { ArrowRight, ArrowLeft, X, BookOpen } from "lucide-react";

const seenKey = (id) => `zl_welcome_${id}`;

// First-login welcome tour for adult roles. Self-gates on role + a per-user
// localStorage flag, and is suppressed while an admin is impersonating.
export default function WelcomeTour() {
  const { user, role, impersonating } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user || impersonating) { setOpen(false); return; }
    if (!TOUR_ROLES.includes(role)) { setOpen(false); return; }
    const seen = localStorage.getItem(seenKey(user.id));
    if (!seen) { setStep(0); setOpen(true); }
  }, [user, role, impersonating]);

  if (!open || !user || !TUTORIALS[role]) return null;

  const guide = TUTORIALS[role];
  const steps = guide.steps;
  const isLast = step === steps.length - 1;
  const s = steps[step];

  const dismiss = () => {
    localStorage.setItem(seenKey(user.id), "1");
    setOpen(false);
  };
  const openGuide = () => { dismiss(); navigate("/help"); };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#1F2937]/60 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="welcome-tour"
      >
        <motion.div
          initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }}
          className="w-full max-w-md overflow-hidden rounded-[28px] bg-white shadow-2xl border border-[#E5DECF]"
        >
          <div className="relative px-7 pt-7 pb-5 text-white" style={{ background: guide.color }}>
            <button onClick={dismiss} data-testid="welcome-skip" className="absolute right-4 top-4 rounded-xl p-1.5 text-white/90 hover:bg-white/20" aria-label="Skip">
              <X size={18} />
            </button>
            <div className="flex items-center gap-3">
              <img src={ZOOD_MASCOT} alt="" className="h-14 w-14 object-contain drop-shadow" />
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-white/80">Welcome to ZoodLeap</div>
                <div className="font-heading text-xl font-extrabold">{step === 0 ? `Hi ${user.name?.split(" ")[0] || "there"}!` : guide.label}</div>
              </div>
            </div>
            <p className="mt-3 font-body text-sm text-white/90">{step === 0 ? guide.tagline : `Step ${step + 1} of ${steps.length}`}</p>
          </div>

          <div className="px-7 py-6">
            <div className="flex items-start gap-3" data-testid={`welcome-step-${step}`}>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl" style={{ background: `${guide.color}1A`, color: guide.color }}>
                <s.icon size={22} />
              </div>
              <div>
                <h3 className="font-heading text-lg font-extrabold text-[#1F2937]">{s.title}</h3>
                <p className="mt-1 font-body text-sm text-[#6B7280]">{s.body}</p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-center gap-1.5">
              {steps.map((_, i) => (
                <span key={i} className="h-1.5 rounded-full transition-all" style={{ width: i === step ? 20 : 6, background: i === step ? guide.color : "#E5DECF" }} />
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                onClick={() => (step === 0 ? dismiss() : setStep((v) => v - 1))}
                data-testid="welcome-back"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-bold text-[#6B7280] hover:bg-[#F2ECE1]"
              >
                {step === 0 ? "Skip" : <><ArrowLeft size={16} /> Back</>}
              </button>
              {isLast ? (
                <div className="flex items-center gap-2">
                  <button onClick={openGuide} data-testid="welcome-view-guide" className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DECF] px-4 py-2.5 text-sm font-bold text-[#1F2937] hover:bg-[#F2ECE1]">
                    <BookOpen size={16} /> Full guide
                  </button>
                  <button onClick={dismiss} data-testid="welcome-done" className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-md" style={{ background: guide.color }}>
                    Get started <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <button onClick={() => setStep((v) => v + 1)} data-testid="welcome-next" className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-md" style={{ background: guide.color }}>
                  Next <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
